import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';

import { FeatureGateService } from 'src/shared/services/feature-gate.service';

import { ApplicationDetailPage } from 'src/pages/leasing/application-detail/application-detail';
import { LeaseLedgerPage } from 'src/pages/leases/lease-ledger/lease-ledger';
import { RoomOperationsPage } from 'src/pages/leasing/room-operations/room-operations';
import { RoomSetupPage } from 'src/pages/leasing/room-setup/room-setup';
import { ChannelDetailComponent } from 'src/pages/marketing/components/channel-detail/channel-detail.component';

import { formatPlatformName, getListingPlatform, LISTING_PLATFORMS } from 'src/shared/constants/listing-platforms';
import { RentalUnitsService, RentalUnit, UnitState, RentableSpace, ComplianceItem } from 'src/shared/services/rental-units.service';
import { RentalApplicationsService, RentalApplication } from 'src/shared/services/rental-applications.service';
import { rentLabel } from 'src/shared/util/rent-label';

import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';

// Precomputed listing chip — one per space.listings entry (CLAUDE.md: no template function calls).
export interface ListingChipView {
  logo: string;
  label: string;
}

// Precomputed vacant-unit listing channel row — platform logo + colored status,
// Active channels first (CLAUDE.md: no template function calls).
export interface ChannelRowView {
  name: string;
  status: string;
  statusColor: string;
  logo: string;
}

// Precomputed per-room view model — no template function calls (CLAUDE.md rule).
// Vacant rooms carry their waitlist applicants; occupied rooms carry nothing extra
// (the lease id is read straight off the space for the ledger link).
export interface RoomView {
  space: RentableSpace;
  isVacant: boolean;
  stateColor: string;
  rentLabel: string;
  vacantDaysLabel: string;
  askingRentLabel: string;
  waitlistApplications: RentalApplication[];
  listingChips: ListingChipView[];
  shareApplicationLinkMessage: string;
}

// Precomputed compliance-checklist row — status color/label/detail all resolved
// up front so the template only binds properties (CLAUDE.md: no template function calls).
export interface ComplianceItemView {
  item: ComplianceItem;
  statusLabel: string;
  statusColor: string;
  isActionNeeded: boolean;
  detailLine: string;
}

@Component({
  templateUrl: 'unit-detail.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UnitDetailPage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private featureGate = inject(FeatureGateService);
  private unitsService = inject(RentalUnitsService);
  private applicationsService = inject(RentalApplicationsService);

  readonly windowService = inject(WindowService);

  isRightSideContent = true;
  dialogParams: any;
  unit: RentalUnit;
  applications: RentalApplication[] = [];
  loaded = false;
  errorMessage: string;
  displayStateLabel = '';
  displayStateColor = '';
  listingChannelViews: ChannelRowView[] = [];
  private panelClosedSubscription: Subscription;

  // By-room precomputed view model (all undefined/false when rental_mode !== 'by_room').
  isByRoom = false;
  derivedState = '';
  occupancyLabel = '';
  combinedRoomRent = 0;
  yieldDelta = 0;
  yieldDeltaLabel = '';
  rooms: RoomView[] = [];
  commonSpaces: RentableSpace[] = [];
  complianceItems: ComplianceItemView[] = [];
  listingsSummaryLabel = '';
  hasRoomListings = false;

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    this.unit = this.navCtrl.getParam('unit') || this.dialogParams?.unit;
    if (this.isRightSideContent && this.unit) {
      this.rightSidePanelService.setDialogPageTitle('Leasing');
    }
    const applicationIds = this.unit?.application_ids ?? [];
    if (this.unit?.state === 'Applications' && applicationIds.length) {
      try {
        const all = await this.applicationsService.getApplications();
        this.applications = all.filter(a => applicationIds.includes(a.id));
      } catch {
        this.applications = [];
      }
    }
    if (this.unit && this.unitsService.isByRoom(this.unit)) {
      await this.buildByRoomViewModel(this.unit);
    }
    if (this.unit) {
      this.displayStateLabel = this.buildDisplayStateLabel();
      // Colored from the state the badge actually shows — for a split unit
      // that's the derived state, which differs from the stored `unit.state`.
      this.displayStateColor = this.stateColor(this.isByRoom ? this.derivedState : this.unit.state);
      this.listingChannelViews = this.buildListingChannelViews(this.unit);
    }
    this.loaded = true;
    this.cdr.markForCheck();
  }

  // "Vacant 9 days" reads as the unit's status, not marketing copy — surface it
  // in the header badge next to the unit name instead of inside the card.
  private buildDisplayStateLabel(): string {
    if (this.isByRoom) { return this.derivedState; }
    if (this.unit.state === 'Vacant' && this.unit.days_vacant) {
      return `Vacant ${this.unit.days_vacant} days`;
    }
    return this.unit.state;
  }

  private buildListingChannelViews(unit: RentalUnit): ChannelRowView[] {
    const channels = unit.listing_channels || [];
    const active = channels.filter(c => c.status === 'Active');
    const rest = channels.filter(c => c.status !== 'Active');
    return [...active, ...rest].map(channel => ({
      name: channel.name,
      status: channel.status,
      statusColor: channel.status === 'Active' ? '#2E7D32' : '#F9A825',
      logo: this.channelLogo(channel.name),
    }));
  }

  private channelLogo(name: string): string {
    const lower = name.toLowerCase();
    const platform = LISTING_PLATFORMS.find(p => lower.includes(p.viewValue.toLowerCase()));
    return platform?.logo || 'assets/svg/globe-outline.svg';
  }

  async buildByRoomViewModel(unit: RentalUnit): Promise<void> {
    this.isByRoom = true;
    this.derivedState = this.unitsService.derivedState(unit);
    this.occupancyLabel = this.unitsService.occupancySummary(unit).label;
    this.combinedRoomRent = this.unitsService.combinedRoomRent(unit);
    this.yieldDelta = this.unitsService.yieldDelta(unit);
    const sign = this.yieldDelta >= 0 ? '+' : '-';
    this.yieldDeltaLabel = `${sign}$${Math.abs(this.yieldDelta).toLocaleString('en-US')}/mo vs whole-unit market rent`;

    const spaces = unit.spaces || [];
    this.commonSpaces = spaces.filter(s => s.type === 'common');

    let allApplications: RentalApplication[] = [];
    const hasWaitlistedRoom = spaces.some(s => s.type === 'room' && (s.application_ids?.length ?? 0) > 0);
    if (hasWaitlistedRoom) {
      try {
        allApplications = await this.applicationsService.getApplications();
      } catch {
        allApplications = [];
      }
    }

    this.rooms = spaces
      .filter(s => s.type === 'room')
      .map(space => {
        const isVacant = !space.tenant_name;
        const applicationIds = space.application_ids ?? [];
        const roomRent = this.roomRentLabel(space);
        return {
          space,
          isVacant,
          stateColor: space.state ? this.stateColor(space.state) : '#9E9E9E',
          rentLabel: roomRent,
          vacantDaysLabel: `Vacant ${space.days_vacant || 0} days`,
          // Blank when rent isn't set yet, so the template never renders a
          // dangling "asking" suffix with no amount before it.
          askingRentLabel: roomRent ? `${roomRent} asking` : '',
          waitlistApplications: isVacant && applicationIds.length
            ? allApplications.filter(a => applicationIds.includes(a.id))
            : [],
          listingChips: this.buildListingChips(space),
          shareApplicationLinkMessage: `Application link copied. Share it to collect applications for ${space.name}.`,
        };
      });

    this.complianceItems = (unit.compliance_checklist || []).map(item => this.buildComplianceItemView(item));

    const allListings = spaces.flatMap(s => s.listings || []);
    const distinctPlatforms = new Set(allListings.map(l => l.platform));
    this.hasRoomListings = allListings.length > 0;
    this.listingsSummaryLabel = `${allListings.length} room listing${allListings.length === 1 ? '' : 's'} across ${distinctPlatforms.size} platform${distinctPlatforms.size === 1 ? '' : 's'}`;
  }

  private buildListingChips(space: RentableSpace): ListingChipView[] {
    return (space.listings || []).map(listing => {
      const platform = getListingPlatform(listing.platform);
      const statusLabel = listing.status.charAt(0).toUpperCase() + listing.status.slice(1);
      return {
        logo: platform?.logo || `assets/img/listing-platforms/${  listing.platform  }.png`,
        label: `${formatPlatformName(listing.platform)} · ${statusLabel}`,
      };
    });
  }

  private buildComplianceItemView(item: ComplianceItem): ComplianceItemView {
    const isActionNeeded = item.status === 'action_needed';
    const statusLabel = item.status === 'complete' ? 'Complete'
      : item.status === 'action_needed' ? 'Action needed'
      : 'Pending';
    const statusColor = item.status === 'complete' ? '#2E7D32'
      : item.status === 'action_needed' ? '#C62828'
      : '#F9A825';
    // Date-only string — `new Date()` would read it as UTC midnight and show
    // the previous day west of UTC.
    const dueLabel = item.due_date ? new ParseDateTimePipe().transform(item.due_date, 'MMM d, yyyy') : '';
    const detailLine = (item.detail || '') + (item.due_date ? `${item.detail ? ' · ' : ''  }Due ${  dueLabel}` : '');
    return { item, statusLabel, statusColor, isActionNeeded, detailLine };
  }

  roomRentLabel(space: RentableSpace): string {
    return rentLabel(space.rent, space.cadence);
  }

  // Widened like the service's own signature, so a derived display state
  // ('Partially occupied') can be colored as well as a stored UnitState.
  stateColor(state: UnitState | string): string {
    return this.unitsService.stateColor(state);
  }

  screeningLabel(application: RentalApplication): string {
    const s = application.screening;
    if (!s || s.status === 'pending') { return 'Screening pending'; }
    switch (s.recommendation) {
      case 'recommended': return 'Recommended';
      case 'review': return 'Review';
      case 'not_recommended': return 'Not recommended';
      default: return 'Pending';
    }
  }

  screeningColor(application: RentalApplication): string {
    const s = application.screening;
    if (!s || s.status === 'pending') { return '#999'; }
    switch (s.recommendation) {
      case 'recommended': return '#2E7D32';
      case 'review': return '#F9A825';
      case 'not_recommended': return '#C62828';
      default: return '#999';
    }
  }

  // --- Actions — no backend behind any of these yet, so they all hand off to the
  // account manager (arrow-fn props so they bind to tidy-button [action]) ---
  shareApplicationLink = () => this.featureGate.gate();
  sendSigningReminder = () => this.featureGate.gate();
  viewLease = () => this.featureGate.gate();
  editCollectionSettings = () => this.featureGate.gate();
  sendRentReminder = () => this.featureGate.gate();
  sendLateNotice = () => this.featureGate.gate();
  startPaymentPlan = () => this.featureGate.gate();
  updateEvictionStage = () => this.featureGate.gate();
  contactAttorney = () => this.featureGate.gate();

  shareRoomApplicationLink(room: RoomView) {
    this.featureGate.gate();
  }

  goToApplicationDetail(application: RentalApplication) {
    this.goToPage('application-detail', { application }, ApplicationDetailPage);
  }

  goToLeaseLedger(space: RentableSpace) {
    if (!space.lease_id) { return; }
    this.goToPage('lease-ledger', { leaseId: space.lease_id }, LeaseLedgerPage);
  }

  goToRoomOperations() {
    if (!this.unit) { return; }
    this.goToPage('room-operations', { unitId: this.unit.id }, RoomOperationsPage);
  }

  goToRoomSetup() {
    if (!this.unit) { return; }
    this.goToPage('room-setup', { unitId: this.unit.id }, RoomSetupPage);
  }

  goToListRoom(space: RentableSpace) {
    if (!this.unit) { return; }
    this.goToPage(
      'revenue/listing-detail',
      { unitId: this.unit.id, spaceId: space.id, addressId: this.unit.address_id },
      ChannelDetailComponent
    );
  }

  goToChannelsTab() {
    this.navCtrl.navigateForward('listings');
  }

  async goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(async () => {
        // A room listing may have been added/updated in the panel (e.g. channel-detail) —
        // rebuild the by-room view model so new listing chips show without a full reload.
        if (this.unit && this.unitsService.isByRoom(this.unit)) {
          await this.buildByRoomViewModel(this.unit);
        }
        this.cdr.markForCheck();
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
